import { useCallback, useState } from 'react';

const getDefaultName = (type, name, isFromModal = false) => {
  if (type === 'catalogs') {
    return name === 'key'
      ? `${isFromModal ? 'saved-' : ''}deliverables`
      : `${isFromModal ? 'Saved ' : ''}Deliverables`;
  }

  return name === 'key' ? type : type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Custom hook for handling tree data operations
 * @returns {Object} Object containing tree data state and handler functions
 */
const useTreeHandler = () => {
  const [treeData, setTreeData] = useState({});
  const [initialTreeData, setInitialTreeData] = useState({});
  /**
   * Add key
   *
   * @param {Object} obj
   * @param {string} pkey
   * @param {number} level
   */
  const addKey = useCallback((obj, pkey = '', level = 1, index = 0) => {
    if (obj && obj.children && obj.children.length) {
      obj.children.forEach((o, i) => {
        const key = `${pkey}children.${o.create ? index : o.index || i}`;
        o.key = `${key}_${level}_${o.title}${o.create ? '_new' : ''}`;
        o.path = key;
        addKey(o, `${key}.`, level + 1, index);
      });
    }

    if (obj && obj.items && obj.items.length) {
      obj.items.forEach((item, i) => {
        item.index = i;
      });
    }
  }, []);

  /**
   * Handle tree data
   *
   * @param {Object} params
   * @param {Object} params.data
   * @param {boolean} skipDefault
   * @param {string} keyToBeSelected
   * @param {boolean} fromModal
   * @param {string} type
   * @param {boolean} isDefaultFolder
   * @param {boolean} isWixServices
   * @param {boolean} isWixProducts
   * @param {Function} onKeySelect
   * @param {Object} toFolderKey
   */
  const handleTreeData = useCallback(
    ({
      data,
      skipDefault,
      keyToBeSelected,
      fromModal,
      type,
      isDefaultFolder,
      isWixServices,
      isWixProducts,
      isPipedriveProducts,
      isHubSpotProducts,
      onKeySelect,
      toFolderKey,
      fromPriceModal,
    }) => {
      if (!skipDefault) {
        data.key = '';
        addKey(data);
        data.children = data?.children?.reverse();
        if (!fromModal) {
          if (data.children) {
            if (type !== 'catalogs') {
              data.children.splice(0, 0, {
                children: null,
                key: 'starred-folder',
                path: 'starred-folder',
                title: 'All Starred',
                default: true,
              });
            }

            if (type === 'templates') {
              data.children.splice(0, 0, {
                children: null,
                key: 'shared-folder',
                path: 'shared-folder',
                title: 'All Shared',
                default: true,
              });
            }

            data.children.splice(0, 0, {
              children: null,
              key: getDefaultName(type, 'key', fromPriceModal),
              path: '',
              title: `All ${getDefaultName(type, 'title', fromPriceModal)}`,
              default: true,
            });

            if (isDefaultFolder) {
              data.children.splice(0, 0, {
                children: null,
                key: 'default-deliverables',
                path: 'default-deliverables',
                title: 'Default Deliverables',
                default: true,
              });
            }

            if (isWixServices) {
              data.children.splice(1, 0, {
                children: null,
                key: 'wix-services',
                path: 'wix-services',
                title: 'Your Wix Services',
                default: true,
              });
            }

            if (isWixProducts) {
              // push to third place if wixservice is there
              if (isWixServices && data.children.length >= 2) {
                data.children.splice(2, 0, {
                  children: null,
                  key: 'wix-products',
                  path: 'wix-products',
                  title: 'Your Wix Products',
                  default: true,
                });
              } else {
                data.children.push({
                  children: null,
                  key: 'wix-products',
                  path: 'wix-products',
                  title: 'Your Wix Products',
                  default: true,
                });
              }
            }

            if (isPipedriveProducts) {
              data.children.splice(1, 0, {
                children: null,
                key: 'pipedrive-products',
                path: 'pipedrive-products',
                title: 'Your Pipedrive Products',
                default: true,
              });
            }

            if (isHubSpotProducts) {
              data.children.splice(1, 0, {
                children: null,
                key: 'hubspot-products',
                path: 'hubspot-products',
                title: 'Your HubSpot Products',
                default: true,
              });
            }

            // Add divider before "All" items if there are integration products
            const integrations = [
              isDefaultFolder,
              isWixServices,
              isWixProducts,
              isPipedriveProducts,
              isHubSpotProducts,
            ];
            const integrationLength = integrations.filter(Boolean).length;
            const hasIntegrationProducts = integrationLength > 0;

            if (hasIntegrationProducts) {
              data.children.splice(integrationLength, 0, {
                children: null,
                key: 'divider-integration',
                path: 'divider-integration',
                title: null,
                default: true,
                isDivider: true,
                className: 'tree-divider-node',
              });
            }
          } else {
            if (isDefaultFolder) {
              data.children.splice(0, 0, {
                children: null,
                key: 'default-deliverables',
                path: 'default-deliverables',
                title: 'Default Deliverables',
                default: true,
              });
            }

            if (isWixServices) {
              data.children.splice(1, 0, {
                children: null,
                key: 'wix-services',
                path: 'wix-services',
                title: 'Wix Services',
                default: true,
              });
            }

            if (isWixProducts) {
              // If Wix Services is present, insert Wix Products at third position; otherwise, push to the end
              if (isWixServices && data.children.length >= 2) {
                data.children.splice(2, 0, {
                  children: null,
                  key: 'wix-products',
                  path: 'wix-products',
                  title: 'Wix Products',
                  default: true,
                });
              } else {
                data.children.push({
                  children: null,
                  key: 'wix-products',
                  path: 'wix-products',
                  title: 'Wix Products',
                  default: true,
                });
              }
            }

            if (isPipedriveProducts) {
              data.children.splice(1, 0, {
                children: null,
                key: 'pipedrive-products',
                path: 'pipedrive-products',
                title: 'Pipedrive Products',
                default: true,
              });
            }

            if (isHubSpotProducts) {
              // Insert HubSpot Products at the second position (index 1)
              data.children.splice(1, 0, {
                children: null,
                key: 'hubspot-products',
                path: 'hubspot-products',
                title: 'HubSpot Products',
                default: true,
              });
            }

            // Add divider before "All" items if there are integration products
            const integrations = [
              isDefaultFolder,
              isWixServices,
              isWixProducts,
              isPipedriveProducts,
              isHubSpotProducts,
            ];
            const integrationLength = integrations.filter(Boolean).length;
            const hasIntegrationProducts = integrationLength > 0;

            if (hasIntegrationProducts) {
              data.children.splice(integrationLength, 0, {
                children: null,
                key: 'divider-integration',
                path: 'divider-integration',
                title: null,
                default: true,
                isDivider: true,
                className: 'tree-divider-node',
              });
            }

            data.children = [
              ...data.children,
              {
                children: null,
                key: getDefaultName(type, 'key', fromPriceModal),
                path: '',
                title: `All ${getDefaultName(type, 'title', fromPriceModal)}`,
                default: true,
              },
            ];

            if (type !== 'catalogs') {
              data.children.push({
                children: null,
                key: 'starred-folder',
                path: 'starred-folder',
                title: 'All Starred',
                default: true,
              });
            }

            if (type === 'templates') {
              data.children.push({
                children: null,
                key: 'shared-folder',
                path: 'shared-folder',
                title: 'All Shared',
                default: true,
              });
            }

            // if (type === 'sections') {
            //   data.children.push({
            //     children: null,
            //     key: 'isHeader',
            //     path: 'isHeader',
            //     title: 'Header',
            //     default: true,
            //   });
            // }
          }
        }
      }

      setTreeData(data);

      if (keyToBeSelected) {
        let key = '';

        for (let contentLibraryTreeItem in data.children) {
          if (data.children[contentLibraryTreeItem].title === keyToBeSelected) {
            key = data.children[contentLibraryTreeItem]?.key;
            break;
          } else if (data.children[contentLibraryTreeItem]?.children?.length) {
            for (let contentLibraryTreeItemChild in data.children[contentLibraryTreeItem]
              ?.children) {
              if (
                data.children[contentLibraryTreeItem]?.children?.[contentLibraryTreeItemChild]
                  .title === keyToBeSelected
              ) {
                key =
                  data.children[contentLibraryTreeItem]?.children?.[contentLibraryTreeItemChild]
                    ?.key;
                break;
              }
            }
          }
        }

        if (key) {
          const k = [key].map((k) => k.split('_'));
          onKeySelect(k);
        }
      } else {
        let key = '';

        for (let contentLibraryTreeItem in data.children) {
          if (
            data.children[contentLibraryTreeItem].path === toFolderKey ||
            data.children[contentLibraryTreeItem].path === toFolderKey?.key
          ) {
            key = data.children[contentLibraryTreeItem]?.key;
            break;
          }
        }

        if (key && !toFolderKey?.treeId) {
          const k = [key].map((k) => k.split('_'));
          onKeySelect(k);
        }
      }
    },
    [addKey]
  );

  /**
   * Set indexes
   *
   * @param {Object} treeData
   */
  const setIndexes = useCallback((treeData) => {
    if (!Array.isArray(treeData?.children)) {
      return treeData;
    }

    for (let i = 0; i < treeData.children.length; i++) {
      treeData.children[i].index = i;
      if (treeData?.children[i]?.children?.length) {
        treeData.children[i] = setIndexes(treeData.children[i]);
      }
    }
    return treeData;
  }, []);

  return {
    treeData,
    setTreeData,
    handleTreeData,
    setIndexes,
    addKey,
    initialTreeData,
    setInitialTreeData,
    getDefaultName,
  };
};

export default useTreeHandler;
